import React from 'react';
import {
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import SectionCard from './SectionCard';
import { providerSupportsStop } from '../../util/provider';
import { TIMER_UNITS, DEFAULT_TIMER_UNIT, DEFAULT_TIMER_VALUE } from '../utils/autoTeardownUnits';

/**
 * Auto teardown timer.
 *
 * Gives a cluster a lifetime: N minutes/hours/days of RUNNING time after it
 * comes up, then Heidi stops or destroys it. Default off. The window restarts
 * on every deploy and every start, so a stopped cluster that is started again
 * gets a fresh one.
 *
 * On a provider that cannot stop a cluster (Oracle), Stop is disabled rather
 * than quietly turned into a destroy - the two are not interchangeable and
 * nobody should lose a cluster to a setting they read as "pause".
 */
export default function ClusterTimerSection({ form, setField, provider, disabled }) {
    const enabled = form.autoTeardownEnabled || false;
    const canStop = providerSupportsStop(provider);
    const action = canStop ? (form.autoTeardownAction || 'stop') : 'destroy';
    const value = form.autoTeardownValue ?? '';

    // A provider that cannot stop has only one possible action - keep the
    // stored value honest so the form never submits a stop it cannot honour.
    React.useEffect(() => {
        if (!canStop && form.autoTeardownAction !== 'destroy') {
            setField('autoTeardownAction', 'destroy');
        }
    }, [canStop, form.autoTeardownAction, setField]);

    // An untouched field is not a mistake yet. Turning the switch on used to
    // paint the duration red before the user could possibly have typed
    // anything, which reads as "you did something wrong" for doing exactly what
    // was asked. Enabling now fills in a duration, so the section opens in a
    // valid state, and the error only appears if the field is actually left
    // empty or nonsensical.
    const [touched, setTouched] = React.useState(false);
    const numericValue = Number(value);
    const invalid = enabled && touched && (value === '' || !isFinite(numericValue) || numericValue <= 0);

    const handleToggle = (checked) => {
        setTouched(false);
        // Only prefill when there is nothing to preserve - never overwrite a
        // duration the user or an existing cluster already set.
        const needsDefault = checked && (value === '' || value === null || value === undefined);
        setField(needsDefault
            ? {
                autoTeardownEnabled: true,
                autoTeardownValue: DEFAULT_TIMER_VALUE,
                autoTeardownUnit: form.autoTeardownUnit || DEFAULT_TIMER_UNIT,
            }
            : { autoTeardownEnabled: checked });
    };

    return (
        <SectionCard
            icon="timer"
            title="Auto Stop / Destroy Timer"
            tooltip="Give this cluster a lifetime. The countdown starts when the cluster becomes available, and restarts every time it is deployed or started again."
        >
            <FormControlLabel
                control={
                    <Switch
                        checked={enabled}
                        onChange={(event) => handleToggle(event.target.checked)}
                        name="autoTeardownEnabled"
                        color="primary"
                        disabled={disabled}
                    />
                }
                label={enabled ? 'Enabled' : 'Disabled'}
            />
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 16 }}>
                Automatically {action === 'destroy' ? 'destroy' : 'stop'} this cluster after it has been
                running for the time set below. Off by default.
            </Typography>

            {enabled && (
                <>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
                        <TextField
                            id="autoTeardownValue"
                            label="Run for"
                            type="number"
                            variant="outlined"
                            value={value}
                            onChange={(event) => {
                                setTouched(true);
                                setField('autoTeardownValue', event.target.value);
                            }}
                            onBlur={() => setTouched(true)}
                            inputProps={{ min: 1, step: 1 }}
                            error={invalid}
                            helperText={invalid ? 'Enter a number greater than 0' : ' '}
                            disabled={disabled}
                            style={{ width: 160 }}
                        />
                        <FormControl variant="outlined" style={{ width: 160 }}>
                            <InputLabel id="autoTeardownUnit-label">Unit</InputLabel>
                            <Select
                                labelId="autoTeardownUnit-label"
                                id="autoTeardownUnit"
                                value={form.autoTeardownUnit || DEFAULT_TIMER_UNIT}
                                onChange={(event) => setField('autoTeardownUnit', event.target.value)}
                                label="Unit"
                                disabled={disabled}
                            >
                                {TIMER_UNITS.map(u => (
                                    <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                        When the time is up
                    </Typography>
                    <RadioGroup
                        name="autoTeardownAction"
                        value={action}
                        onChange={(event) => setField('autoTeardownAction', event.target.value)}
                    >
                        <FormControlLabel
                            value="stop"
                            control={<Radio color="primary" />}
                            disabled={disabled || !canStop}
                            label={
                                <span>
                                    Stop the cluster
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        {canStop
                                            ? 'Powers the head node off. Compute billing stops, storage is kept, and the cluster can be started again.'
                                            : 'Not available: this provider cannot stop a cluster, only destroy it.'}
                                    </Typography>
                                </span>
                            }
                        />
                        <FormControlLabel
                            value="destroy"
                            control={<Radio color="primary" />}
                            disabled={disabled}
                            label={
                                <span>
                                    Destroy the cluster
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Tears the cluster down completely. Anything not saved elsewhere is lost.
                                    </Typography>
                                </span>
                            }
                        />
                    </RadioGroup>
                </>
            )}
        </SectionCard>
    );
}
